import { BellIcon } from "@heroicons/react/24/outline";
import { BellSlashIcon as BellIconSolid } from "@heroicons/react/24/solid";
import {
  FavoriteSearch,
  useGetFavoriteSearchesByVehicleQuery,
} from "../graphql/generated/graphql";
import useFavoriteSearchMutations from "../hooks/useFavoriteSearchMutations";
import {
  getFavoriteSearchesByVehiclePayload,
  getFavoriteSearchQuerystringFromVehicleData,
} from "../utils/favoriteSearches";
import Spinner from "./Spinner";
import { classNames } from "../utils/formatters";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { FavoriteSearchesList } from "./FavoriteSearches";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";

type TProps = {
  refetchVehicleSearches: () => void;
  favoriteSearches: FavoriteSearch[];
  open?: boolean;
  setOpen: (open?: boolean) => void;
};

function VehicleNotificationsModal({
  refetchVehicleSearches,
  favoriteSearches,
  open,
  setOpen,
}: TProps) {
  useEffect(() => {
    if (!favoriteSearches?.length) {
      setOpen(false);
    }
  }, [favoriteSearches]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                <div className="text-center">
                  <Dialog.Title as="h3" className="leading-6 ">
                    Administrar notificaciones
                  </Dialog.Title>
                </div>
                <div className="mt-2">
                  <FavoriteSearchesList
                    searches={favoriteSearches}
                    refetchVehicleSearches={refetchVehicleSearches}
                  />
                </div>
                <div className="flex flex-col gap-x-1">
                  <button
                    type="button"
                    className="mt-2 inline-flex w-full justify-center rounded-md border border-gray-300 hover:border-gray-400 bg-white px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-600 shadow-sm focus:outline-none focus:ring-2  focus:ring-offset-2 sm:text-sm"
                    onClick={() => setOpen(false)}
                  >
                    Cancelar
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default function VehicleNotifyButton({ queryObject }) {
  const { status: sessionStatus } = useSession();
  const router = useRouter();

  const [isNotificationsModalOpen, setNotificationsModalOpen] = useState(false);

  const {
    data: favoriteSearchesData,
    loading: loadingFavoriteSearches,
    refetch: refetchVehicleSearches,
  } = useGetFavoriteSearchesByVehicleQuery({
    variables: { vehicle: getFavoriteSearchesByVehiclePayload(queryObject) },
    fetchPolicy: "cache-and-network",
    skip: sessionStatus !== "authenticated",
  });

  const { addFavoriteSearch, updateFavoriteSearch, updatingFavoriteSearch } =
    useFavoriteSearchMutations(refetchVehicleSearches);

  const { getFavoriteSearchesByVehicle } = favoriteSearchesData || {};
  const hasActiveNotifications = getFavoriteSearchesByVehicle?.filter(
    (s) => s.notify
  ).length;

  function disableNotifications() {
    if (sessionStatus === "unauthenticated") {
      router.push("/auth/signin");
    } else {
      if (getFavoriteSearchesByVehicle?.length === 1) {
        updateFavoriteSearch(
          getFavoriteSearchesByVehicle[0]._id,
          !getFavoriteSearchesByVehicle[0].notify
        );
      } else {
        setNotificationsModalOpen(true);
      }
    }
  }

  function enableNotifications() {
    if (sessionStatus === "unauthenticated") {
      router.push("/auth/signin");
    } else {
      if (!getFavoriteSearchesByVehicle?.length) {
        const notify = true;
        addFavoriteSearch(
          getFavoriteSearchQuerystringFromVehicleData(queryObject),
          notify
        );
      } else {
        setNotificationsModalOpen(true);
      }
    }
  }

  const FavoriteSearchIcon = hasActiveNotifications ? BellIconSolid : BellIcon;

  return (
    <div className="flex items-center mt-1">
      {updatingFavoriteSearch || loadingFavoriteSearches ? (
        <Spinner className="h-5 w-5" />
      ) : (
        <FavoriteSearchIcon
          className={classNames(
            hasActiveNotifications ? "text-blue-500" : "text-gray-900",
            "h-5 w-5 flex-shrink-0"
          )}
          aria-hidden="true"
        />
      )}
      <button
        className="ml-2 text-sm font-medium text-orange-500 hover:text-orange-400 cursor-pointer disabled:opacity-50 text-left"
        onClick={
          hasActiveNotifications ? disableNotifications : enableNotifications
        }
        disabled={updatingFavoriteSearch || loadingFavoriteSearches}
      >
        {hasActiveNotifications
          ? "Silenciar notificaciones"
          : "Notificarme publicaciones similares"}
      </button>
      <VehicleNotificationsModal
        refetchVehicleSearches={refetchVehicleSearches}
        favoriteSearches={getFavoriteSearchesByVehicle}
        open={isNotificationsModalOpen}
        setOpen={setNotificationsModalOpen}
      />
    </div>
  );
}
